import { useAsyncError } from '@common/hooks/useAsyncError';
import { Permission } from '@common/permissions';
import React, { useEffect, useState } from 'react';
import { usePermissions } from 'src/apps/invest/common/hooks/usePermissions';
import { useSession } from 'src/apps/invest/common/hooks/useSession';
import { PageNotFound } from 'src/apps/invest/common/PageNotFound';
import { PAGES } from '../constants';
import { useCheckoutCashflowViewModel } from '../models/CheckoutCashflowViewModel';
import { CheckoutCashflowAmountPage } from '../views/pages/CheckoutCashflowAmountPage';
import { CheckoutCashflowConfirmationPage } from '../views/pages/CheckoutCashflowConfirmationPage';
import { CheckoutCashflowCongratsPage } from '../views/pages/CheckoutCashflowCongratsPage';
import { CheckoutCashflowErrorPage } from '../views/pages/CheckoutCashflowErrorPage';
import { CheckoutCashflowLoadingPage } from '../views/pages/CheckoutCashflowLoadingPage';
import { CheckoutCashflowPricingPlanPage } from '../views/pages/CheckoutCashflowPricingPlanPage';
import { InvestorAccountModel } from '@common/models/InvestorAccountModel';

const sessionRedirect = `${window.location.pathname}${window.location.search}`;
const permissions: Permission[] = ['ACCREDITED_INVESTOR', 'UNACCREDITED_INVESTOR'];

export const CheckoutCashflowController: React.FC = () => {
  const { checkAuthenticated } = useSession();
  const { checkPermissions } = usePermissions();

  const viewModel = useCheckoutCashflowViewModel();
  const [isLoading, setIsLoading] = useState(true);
  const throwError = useAsyncError();

  async function init() {
    if (!(await checkAuthenticated(sessionRedirect))) return;
    if (!(await checkPermissions(permissions))) return;

    // First fetch the offers
    await InvestorAccountModel.getState().fetchCashFlowOffers();

    // Then initialize the view model
    const searchParams = new URLSearchParams(window.location.search);
    const ai = searchParams.get('ai');
    if (ai) {
      await viewModel.initController({ existingAutoInvest: ai });
    } else {
      await viewModel.initController();
    }
    setIsLoading(false);
  }

  useEffect(() => {
    init().catch((err) => throwError(err));
  }, []);

  if (isLoading) return <CheckoutCashflowLoadingPage />;

  switch (viewModel.page) {
    case PAGES.amount:
      return <CheckoutCashflowAmountPage />;
    case PAGES.confirmation:
      return <CheckoutCashflowConfirmationPage />;
    case PAGES.congrats:
      return <CheckoutCashflowCongratsPage />;
    case PAGES.error:
      return <CheckoutCashflowErrorPage />;
    case PAGES.pricingPlan:
      return <CheckoutCashflowPricingPlanPage />;
    default:
      return <PageNotFound />;
  }
};
